import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { differenceInCalendarDays } from 'date-fns';

import { DateSelector } from '../../../components/date-selector/date-selector';
import { fromLocalDate, type PlainDate } from '../../../core/services/plain-date';

import { TaskStatus, type Task } from '../../../app/entities';

import styles from './task-date-selector.module.scss';
type DateSelectorProps = {
  /** A legacy instant string: `Task.dueDate` is not migrated to `PlainDate`. */
  date?: string | null;
  status: Task['status'];
  /**
   * The calendar day the producer picked, or `null` when they cleared it. `Task`'s API path
   * cannot take a bare `YYYY-MM-DD`, so callers must send it back through `toLegacyInstant`.
   */
  onUpdate: (date: PlainDate | null) => void;
  isViewOnly?: boolean;
};

export const TaskDateSelector: React.FC<DateSelectorProps> = observer(
  ({ date, onUpdate, isViewOnly, status }) => {
    const [dateIconType, setDateIconType] = useState<'default' | 'danger' | 'warning'>('default');

    useEffect(() => {
      if (!date || [TaskStatus.Done, TaskStatus.Canceled].includes(status)) {
        return setDateIconType('default');
      }

      const today = new Date();
      const dueDate = new Date(date);

      const dateDifference = differenceInCalendarDays(dueDate, today);

      if (dateDifference <= 0) {
        setDateIconType('danger');
      } else if (dateDifference < 4) {
        setDateIconType('warning');
      } else {
        setDateIconType('default');
      }
    }, [date, status]);

    // `Task.dueDate` isn't migrated to `PlainDate` by this plan; it still arrives as a full
    // instant string. `fromLocalDate(new Date(date))` reads exactly the same local
    // year/month/day getters the previous `format(new Date(date), ...)` call relied on, so the
    // calendar day shown here is unchanged for every viewer — it's a type-level bridge, not a
    // semantic one.
    return (
      <DateSelector
        placeholder="Set due date"
        field="dueDate"
        date={date ? fromLocalDate(new Date(date)) : undefined}
        onUpdate={onUpdate}
        disabled={isViewOnly}
      />
    );
  },
);
